// NOTE all models consists of id, deviceTypeName, description by default

import { SwitchMode } from '../components/DeviceAsDom/Switch';
import { WetDryMode } from '../components/DeviceAsDom/Diverter';
import {
  IconDirection,
  MixerType,
} from './BatchTypeEnums';
import { isFeatureEnabled } from '../utils/feature-flags';
import { PERMISSION } from '../modules/newAuth/model/User';
import { Modules } from '../utils/module-controller';
import { Tenants } from './Tenants';

const FEATURE_AUTO_TUNING = isFeatureEnabled('auto_tuning');
const FEATURE_ALLOW_OVERCAPACITY_IN_ONE_DROP = isFeatureEnabled('allow_overcapacity_in_one_drop');
const FEATURE_LINKED_PNO = isFeatureEnabled('linked_pno');

export const IconList = {
  DEFAULT: 'DEFAULT',
  NONE: 'NONE',
};

export const IconBinList = {
  DEFAULT: 'DEFAULT',
  NONE: 'NONE',
  SCREW: 'SCREW',
};

export const AnalogIconList = {
  BOLT: 'BOLT',
  DROP: 'DROP',
  EXCLAMATION: 'EXCLAMATION',
  PLUG: 'PLUG',
  SPEAKER: 'SPEAKER',
  NONE: 'NONE',
};

export const AnalogUnitList = {
  '%': '%',
  'kW': 'kW',
  'Slump': 'Slump',
  'A': 'A',
  'V': 'V',
  'I': 'I',
  'W': 'W',
  'R': 'R',
};

export const ReorderLevelType = {
  'Unit': 'Unit',
  'Percent': 'Percent',
};

export const DoubleGatesOpenMode = {
  'OpenGatesTogether': 1,
  'OpenGateOneFirst': 2,
  'OpenGateTwoFirst': 3,
};

export const SecondGateOpenDelayUnit = {
  'Second': 'sec',
  'Percent': '%',
};

export const Parity = {
  None: 'None',
  Even: 'Even',
  Odd: 'Odd',
  Mark: 'Mark',
  Space: 'Space',
};

export const BaudRate = {
  1200: 1200,
  2400: 2400,
  4800: 4800,
  9600: 9600,
  19200: 19200,
};

export const DisplayMode = {
  'ASCII': 1,
  'HEX': 2,
};

export const VibratorMode = {
  MinFlow: 'VibOnLowFlow', //1
  SetPoint: 'VibOnFastFlow', //2
};

export const DeviceTypes = {
  Aerator: 'Aerator',
  AnalogInput: 'AnalogInput',
  AnalogSwitch: 'AnalogSwitch',
  Belt: 'Belt',
  Bin: 'Bin',
  Boot: 'Boot',
  Diverter: 'Diverter',
  DustCollector: 'DustCollector',
  // Hoist: 'Hoist',
  Horn: 'Horn',
  ManualPanel: 'ManualPanel',
  Mixer: 'Mixer',
  Pump: 'Pump',
  RemoteDisplay: 'RemoteDisplay',
  Scale: 'Scale',
  Scoreboard: 'Scoreboard',
  Screw: 'Screw',
  // SlumpMeter: 'SlumpMeter',
  Switch: 'Switch',
  Tank: 'Tank',
  Vibrator: 'Vibrator',
  TruckDevice: 'TruckDevice',
};

export const Aerator = {
  switchPNo: { type: 'outputPorts', required: true },
  autoTurnOn: { type: 'boolean', defaultValue: true },
  turnOffDuringJogging: { type: 'boolean', defaultValue: false },
  icon: { type: 'iconList', defaultValue: IconList.DEFAULT },
  emergencyExemption: { type: 'boolean', defaultValue: false },
  linkedPNos: {
    label: 'Linked PNos',
    type: 'array',
    col: 12,
    defaultValue: FEATURE_LINKED_PNO ? {
      sourceField: '',
      targetPNo: 0,
    } : [],
    fieldOptions: {
      sourceField: [ 'switchPNo' ],
    },
    hidden: !FEATURE_LINKED_PNO,
  },
};

export const Belt = {
  startMode: { type: 'beltStartMode', required: true },
  onPNo: { type: 'outputPorts', required: true },
  offPNo: { type: 'outputPorts' },
  interlockStatusPNo: { type: 'inputPorts' },
  runningStatusPNo: { type: 'inputPorts' },
  onDelay: { type: 'number', unit: 'sec', step: 1, max: 25, defaultValue: 0 },
  offDelay: { type: 'number', unit: 'sec', step: 1, max: 1200, defaultValue: 0 },
  hornId: { type: 'deviceHornIds', label: 'Horn' },
  parentBeltId: { type: 'deviceBeltIds', label: 'Belt' },
  direction: { type: 'iconDirection', defaultValue: IconDirection.Right, required: true },
  size: { type: 'number', defaultValue: 5, min: 3, max: 15, step: 1, required: true },
  icon: { type: 'iconList', defaultValue: IconList.DEFAULT },
  emergencyExemption: { type: 'boolean', defaultValue: false },
  keepOnBetweenDrops: { type: 'boolean' },
  autoTurnOn: { type: 'boolean' },
  linkedPNos: {
    label: 'Linked PNos',
    type: 'array',
    col: 12,
    defaultValue: FEATURE_LINKED_PNO ? {
      sourceField: '',
      targetPNo: 0,
    } : [],
    fieldOptions: {
      sourceField: [ 'onPNo' ],
    },
    hidden: !FEATURE_LINKED_PNO,
  },
};

export const FIELDS_FOR_TENANTS = [Tenants.IMI, Tenants.CBDEMO];

export const Bin = {
  vibratorId: 'deviceVibratorIds',
  aeratorId: 'deviceAeratorIds',
  screwId: 'deviceScrewIds',
  scaleId: { type: 'deviceScaleIds' },
  analogInputId: 'deviceAnalogInputIds',
  beltId: 'deviceBeltIds',
  gateOnePNo: { type: 'outputPorts' },
  // gateTwoPNo: 'outputPorts',
  gateCloseStatusPNo: { type: 'inputPorts', required: true },
  // materialLowAlarmPNo: 'outputPorts',
  // lowAlarmType: 'lowAlarmType',
  // lowAlarmDuration: { type: 'number', unit: 'sec' },
  // materialOverAlarmPNo: 'outputPorts',
  // overAlarmType: 'overAlarmType',
  // overAlarmPNo: 'outputPorts',
  initialOpen: { type: 'number', label: 'Decum Initial Open', defaultValue: 0.8, dependsOn: 'scaleId', unit: 'sec', min: 0, max: 10, step: 0.01 },
  openDuration: { type: 'number', label: 'Decum Open Duration', defaultValue: 0.08, dependsOn: 'scaleId', unit: 'sec', min: 0, max: 10, step: 0.01 },
  closeDuration: { type: 'number', label: 'Decum Close Duration', defaultValue: 0.08, dependsOn: 'scaleId', unit: 'sec', min: 0, max: 10, step: 0.01 },
  freeFall: { type: 'number', unit: 'scale unit', min: -Number.MAX_SAFE_INTEGER, step: 1, defaultValue: 0, disabled: 'autoTuning' },
  smallFreeFall: { type: 'number', unit: 'scale unit', min: -Number.MAX_SAFE_INTEGER, step: 1, defaultValue: 0, disabled: 'autoTuning' },
  autoCutOff: { type: 'boolean', disabled: 'autoTuning' },
  autoCutOffAdjustment: { type: 'number', unit: 'scale unit', disabled: 'autoTuning' },
  jogOpenTime: { type: 'number', unit: 'sec', min: 0, max: 10, step: 0.01, defaultValue: 0.7, disabled: 'autoTuning' },
  jogCloseTime: { type: 'number', unit: 'sec', min: 0, max: 10, step: 0.01, defaultValue: 1, disabled: 'autoTuning' },
  smallJogOpenTime: { type: 'number', unit: 'sec', min: 0, max: 10, step: 0.01, defaultValue: 0.5, disabled: 'autoTuning' },
  smallJogCloseTime: { type: 'number', unit: 'sec', min: 0, max: 10, step: 0.01, defaultValue: 0.7, disabled: 'autoTuning' },
  autoJog: { type: 'boolean', disabled: 'autoTuning' },
  autoTuning: { type: 'boolean', label: 'BatchGo AI', hidden: !FEATURE_AUTO_TUNING, permission: PERMISSION.BATCH_AI },
  autoJogOpenTime: { type: 'number', unit: 'sec', hidden: true },
  autoFreeFall: { type: 'number', unit: 'scale unit', disabled: true, dependsOn: 'autoTuning', showForTenants: FIELDS_FOR_TENANTS },
  dynamicFreeFall: { type: 'boolean', hiddenModule: Modules.DYNAMIC_FREE_FALL },
  dynamicFreeFallStart: { type: 'number', hiddenModule: Modules.DYNAMIC_FREE_FALL },
  dynamicFreeFallEnd: { type: 'number', hiddenModule: Modules.DYNAMIC_FREE_FALL },
  autoJogToleranceBelow: { type: 'number', unit: '%', dependsOn: 'autoJog', step: 1, min: 0, max: 80, defaultValue: 0, disabled: 'autoTuning' },
  autoJogToleranceAbove: { type: 'number', unit: '%', dependsOn: 'autoJog', step: 1, min: 0, max: 80, defaultValue: 0, disabled: 'autoTuning' },
  delay: { type: 'number', unit: 'sec', defaultValue: 0 },
  capacity: { type: 'number', defaultValue: 0 },
  reorderLevel: { type: 'number', defaultValue: 0 },
  reorderLevelType: { type: 'reorderLevelList', defaultValue: ReorderLevelType.Unit },
  icon: { type: 'iconBinList', defaultValue: IconBinList.DEFAULT },
  hide: { type: 'boolean' },
  linkedPNos: {
    label: 'Linked PNos',
    type: 'array',
    col: 12,
    defaultValue: FEATURE_LINKED_PNO ? {
      sourceField: '',
      targetPNo: 0,
    } : [],
    fieldOptions: {
      sourceField: [ 'gateOnePNo' ],
    },
    hidden: !FEATURE_LINKED_PNO,
  },
};

export const Boot = {
  upPNo: { type: 'outputPorts', required: true },
  downPNo: 'outputPorts',
  downStatusPNo: 'inputPorts',
  delay: { type: 'number', min: 0, max: 100, defaultValue: 1, unit: 'sec' },
  duration: { type: 'number', min: 0, max: 100, defaultValue: 2, unit: 'sec' },
  icon: { type: 'iconList', defaultValue: IconList.DEFAULT },
  emergencyExemption: { type: 'boolean', defaultValue: false },
  autoTurnOn: { type: 'boolean', defaultValue: true },
  linkedPNos: {
    label: 'Linked PNos',
    type: 'array',
    col: 12,
    defaultValue: FEATURE_LINKED_PNO ? {
      sourceField: '',
      targetPNo: 0,
    } : [],
    fieldOptions: {
      sourceField: [ 'upPNo', 'downPNo' ],
    },
    hidden: !FEATURE_LINKED_PNO,
  },
};

export const Diverter = {
  gateOnePNo: { type: 'outputPorts', required: true },
  mixerId: 'deviceMixerIds',
  mode: { type: 'wetDryMode', defaultValue: WetDryMode.NO, required: true },
  emergencyExemption: { type: 'boolean', defaultValue: false },
};

export const DustCollector = {
  switchPNo: { type: 'outputPorts', required: true },
  keepOnBetweenLoads: { type: 'boolean' },
  emergencyExemption: { type: 'boolean', defaultValue: false },
};

export const Hoist = {
  upPNo: { type: 'outputPorts', required: true },
  downPNo: 'outputPorts',
  upStatusPNo: 'inputPorts',
  downStatusPNo: 'inputPorts',
  emptyStatusPNo: 'inputPorts',
  enableStatusPNo: 'inputPorts',
  openGatePNo: 'outputPorts',
  closeGatePNo: 'outputPorts',
  openGateDuration: { type: 'number', unit: 'sec', min: 0, max: 100, defaultValue: 0 },
  closeGateDuration: { type: 'number', unit: 'sec', min: 0, max: 100, defaultValue: 0 },
  emergencyExemption: { type: 'boolean', defaultValue: false },
};

export const Horn = {
  switchPNo: { type: 'outputPorts', required: true },
  delayOne: { type: 'number', unit: 'sec', min: 0, max: 100, defaultValue: 1 },
  delayOneDuration: { type: 'number', unit: 'sec', min: 0, max: 100, defaultValue: 2 },
  delayTwo: { type: 'number', unit: 'sec', min: 0, max: 100, defaultValue: 0 },
  delayTwoDuration: { type: 'number', unit: 'sec', min: 0, max: 100, defaultValue: 0 },
  delayThree: { type: 'number', unit: 'sec', min: 0, max: 100, defaultValue: 0 },
  delayThreeDuration: { type: 'number', unit: 'sec', min: 0, max: 100, defaultValue: 0 },
  emergencyExemption: { type: 'boolean', defaultValue: false },
  icon: { type: 'iconList', defaultValue: IconList.DEFAULT },
  autoTurnOn: { type: 'boolean', defaultValue: true },
  linkedPNos: {
    label: 'Linked PNos',
    type: 'array',
    col: 12,
    defaultValue: FEATURE_LINKED_PNO ? {
      sourceField: '',
      targetPNo: 0,
    } : [],
    fieldOptions: {
      sourceField: [ 'switchPNo' ],
    },
    hidden: !FEATURE_LINKED_PNO,
  },
};

export const ManualPanel = {
  // startBatchEnablePNo: 'outputPorts',
  // startDischEnablePNo: 'outputPorts',
  // plantErrorPNo: 'outputPorts',
  // batchResetPNo: 'outputPorts',
  startDischPNo: { type: 'inputPorts', label: 'Auto On PNo' },
  emergencyExemption: { type: 'boolean', defaultValue: false },
};

export const Mixer = {
  type: { type: 'mixerType', required: true },
  openTiltPNo: { type: 'outputPorts', required: true },
  closeReturnPNo: { type: 'outputPorts', required: true },
  openTiltStatusPNo: { type: 'inputPorts', required: true },
  closeReturnStatusPNo: { type: 'inputPorts', required: true },
  mixerOnPNo: { type: 'outputPorts' },
  mixerOffPNo: { type: 'outputPorts', dependsOn: 'type', dependsOnOneOf: [ MixerType.Central ] },
  diverterPNo: { type: 'outputPorts' },
  dischargeSequenceId: { type: 'sequences', label: 'Discharge Sequence' },
  tWaterMeterStatusPNo: { type: 'number', label: 'Temper Water P No' }, // pump meterPNo
  dischreadyLightPNo: { type: 'outputPorts', label: 'Discharge Ready P No' },
  analogSwitchId: { type: 'deviceAnalogSwitchIds', label: 'Analog Switch' },
  allowDischargeAt: { type: 'number', dependsOn: 'analogSwitchId', defaultValue: 0 },
  mixingTime: { type: 'number', unit: 'sec', min: 0, max: 1000, defaultValue: 10 },
  topOpenTime: { type: 'number', unit: 'sec', min: 0, max: 1000, defaultValue: 5 },
  downCloseDelay: { type: 'number', unit: 'sec', min: 0, max: 100, defaultValue: 5 },
  switchingDelay: { type: 'number', unit: 'sec', min: 0, max: 100, defaultValue: 5 },
  chargingRate: { type: 'number', defaultValue: 0 },
  mixingStartPoint: { type: 'mixingStartPoint', noEmptyOption: true },
  analogInputId: { type: 'deviceAnalogInputIds', label: 'Analog Input' },
  allowFinishAt: { type: 'number', label: 'Allow Finish At', dependsOn: 'analogInputId', defaultValue: 0 },
  noseplugInputPNo: { type: 'inputPorts' },
  noseplugOutputPNo: { type: 'outputPorts' },
  noseplugDelay: { type: 'number', unit: 'sec', min: 0, max: 100 },
  automaticMode: { type: 'boolean', defaultValue: false },
  multibatch: { type: 'boolean', defaultValue: false },
  emergencyExemption: { type: 'boolean', defaultValue: false },
};

export const AnalogInput = {
  // scaleId: 'deviceScaleIds',
  channelPNo: { type: 'analogInputPorts' },
  unit: { type: 'analogUnitList', defaultValue: AnalogUnitList.kW },
  zero: { type: 'number', defaultValue: -20 },
  span: { type: 'number', defaultValue: 20 },
  icon: { type: 'analogIconList', defaultValue: AnalogIconList.DROP },
  emergencyExemption: { type: 'boolean' },
  useNewFormula: { type: 'boolean' },
};

export const AnalogSwitch = {
  channelPNo: { type: 'analogOutputPorts' },
  // zero: { type: 'number', defaultValue: 0 },
  // span: { type: 'number', defaultValue: 100 },
  divisions: { type: 'number', defaultValue: 5 },
  emergencyExemption: { type: 'boolean', defaultValue: false },
  rampUps: { type: 'array', col: 8, defaultValue: {time: 5, value: 20} },
};

export const SlumpMeter = {
  slumpPNo: { type: 'weightPorts' },
  zeroOffset: 'number',
  calibration: 'number',
  emergencyExemption: { type: 'boolean', defaultValue: false },
};

export const Pump = {
  gatePNo: { type: 'outputPorts', required: true },
  meterPNo: { type: 'meterPorts', required: true },
  bottleGatePNo: { type: 'outputPorts', hideOn: 'tankId' },
  emptyStatusPNo: { type: 'inputPorts', hideOn: 'tankId' },
  tankId: { type: 'deviceTankIds', hideOn: 'bottleGatePNo' },
  capacity: { type: 'number', unit: 'unit',  min: 0 },
  reorderLevel: { type: 'number', defaultValue: 0 },
  reorderLevelType: { type: 'reorderLevelList', defaultValue: ReorderLevelType.Unit },
  unitId: { type: 'meteredUnits', label: 'Unit' },
  linkedPNos: {
    label: 'Linked PNos',
    type: 'array',
    col: 12,
    defaultValue: FEATURE_LINKED_PNO ? {
      sourceField: '',
      targetPNo: 0,
    } : [],
    fieldOptions: {
      sourceField: [ 'gatePNo' ],
    },
    hidden: !FEATURE_LINKED_PNO,
  },
};

export const RemoteDisplay = {
  // address: 'number',
  portCOM: { type: 'number', defaultValue: 0, step: 1 },
  baudRate: { type: 'baudRate', defaultValue: BaudRate['9600'] },
  dataBits: { type: 'number', defaultValue: 8, step: 1 },
  stopBits: { type: 'number', defaultValue: 1, step: 1 },
  parity: { type: 'parity', defaultValue: Parity.None },
  streamInterval: { type: 'number', defaultValue: 1, unit: 'sec', min: 0.1, max: 5 },
  promptSwitchInterval: { type: 'number', defaultValue: 1, unit: 'sec', min: 1, max: 5 },
  // displayMode: 'displayMode',
  promptOne: { type: 'text', col: 12, defaultValue: '<STX><LIGHT:@><TEXT:N=%><ETX>' },
  promptTwo: { type: 'text', col: 12, defaultValue: '<STX><LIGHT:@><TEXT:T=%><ETX>' },
  weight: { type: 'text', col: 12, defaultValue: '<STX><LIGHT:@><TEXT:B=%><ETX>' },
  discharge: { type: 'text', col: 12, defaultValue: '<STX><LIGHT:0><TEXT:W=%><ETX>' },
  idle: { type: 'text', col: 12, defaultValue: '<STX><LIGHT: ><TEXT:  ><ETX>' },
  emergencyExemption: { type: 'boolean', defaultValue: false },
};

export const Scale = {
  vibratorId: 'deviceVibratorIds',
  aeratorId: 'deviceAeratorIds',
  beltId: 'deviceBeltIds',
  screwId: 'deviceScrewIds',
  remoteDisplayId: 'deviceRemoteDisplayIds',
  parentScaleId: 'deviceScaleIds',
  openGateOnePNo: { type: 'outputPorts', required: true },
  closeGateOnePNo: { type: 'outputPorts' },
  gateOneStatusPNo: { type: 'inputPorts', required: true },
  openGateTwoPNo: { type: 'outputPorts' },
  closeGateTwoPNo: { type: 'outputPorts' },
  gateTwoStatusPNo: { type: 'inputPorts' },
  openGateThreePNo: { type: 'outputPorts' },
  closeGateThreePNo: { type: 'outputPorts' },
  gateThreeStatusPNo: { type: 'inputPorts' },
  inputPNo: { type: 'weightPorts', label: 'Channel PNo', required: true },
  interlockStatusPNo: { type: 'inputPorts' },
  doubleGatesOpenMode: { type: 'doubleGatesOpenMode' },
  secondGateOpenDelay: { type: 'number', label: 'Open 2nd Gate Delay', min: 0, max: 100 },
  secondGateOpenDelayUnit: { type: 'secondGateOpenDelayUnit', label: 'Open 2nd Gate Delay Unit' },
  switchGateTypePNo: { type: 'outputPorts' },
  unitId: { type: 'scaleUnits', label: 'Unit', defaultValue: 33, required: true },
  capacity: { type: 'number', unit: 'unitId', defaultValue: 30000, required: true, min: 0 },
  maximumLoad: { type: 'number', unit: 'unitId', defaultValue: 30000, required: true, min: 0 },
  allowOvercapacityInOneDrop: { type: 'boolean', hidden: !FEATURE_ALLOW_OVERCAPACITY_IN_ONE_DROP },
  flowRateTolerance: { type: 'number', label: 'Flow Rate Tolerance %', max: 100, min: 0, step: 1, defaultValue: 10},
  hysteresis: { type: 'number', label: 'Hysteresis', unit: 'sec', max: 10, min: 0, step: 0.1, defaultValue: 1 },
  minimumDischargeRate: { type: 'number', unit: [ 'unitId', 'sec' ], defaultValue: 50, min: 0 },
  startTare: { type: 'number', unit: 'unitId', defaultValue: 10, min: 0 },
  dischargeTare: { type: 'number', unit: 'unitId', defaultValue: 10, min: 0 },
  startTareDistribution: { type: 'boolean' },
  metricDischargeTare: { type: 'number', label: 'Disch Target Tolerance', unit: 'unitId', defaultValue: 10, min: 0 },
  settlingTime: { type: 'number', label: 'Settling Time', unit: 'sec', defaultValue: 2, min: 0, max: 10 },
  dischargeCloseDelay: { type: 'number', unit: 'sec', defaultValue: 3, min: 0, step: 1 },
  dischargeOpenDelay: { type: 'number', unit: 'sec', defaultValue: 2, min: 0, max: 50, step: 1 },
  dischargeOpen: { type: 'number', label: 'Initial Open', unit: 'sec', defaultValue: 0.8, min: 0, max: 10, step: 0.01 },
  initialAdjustDelay: { type: 'number', unit: 'sec', defaultValue: 0, min: 0, max: 10, step: 0.01 },
  vibrationOn: { type: 'number', unit: 'unitId', defaultValue: 500, min: 0 },
  fullOpen: { type: 'number', unit: 'unitId', defaultValue: 1000, min: 0 },
  openDuration: { type: 'number', unit: 'sec', min: 0, max: 10, defaultValue: 0.08, step: 0.01 },
  closeDuration: { type: 'number', unit: 'sec', min: 0, max: 10, defaultValue: 0.08, step: 0.01 },
  graduation: { type: 'number', unit: 'unitId', min: 0 },
  dischargeAfterScaleId: { type: 'deviceScaleIds', label: 'Discharge After Scale', dependsOn: 'parentScaleId' },
  dischargeAfterScaleDelay: { type: 'number', label: 'After Scale Delay', dependsOn: 'parentScaleId', defaultValue: 0, unit: 'sec' },
  dischargeUpperScalesTogether: { type: 'boolean' },
  noFlowControl: { type: 'boolean' },
  decumulative: { type: 'boolean' },
  holdDischargeUntilThereIsEnoughMaterial: { type: 'boolean', dependsOn: 'decumulative' },
  openGateOneAdditionalPNo: { type: 'outputPorts', dependsOn: 'decumulative' },
  closeGateOneAdditionalPNo: { type: 'outputPorts', dependsOn: 'decumulative' },
  openGateTwoAdditionalPNo: { type: 'outputPorts', dependsOn: 'decumulative' },
  closeGateTwoAdditionalPNo: { type: 'outputPorts', dependsOn: 'decumulative' },
  openGateThreeAdditionalPNo: { type: 'outputPorts', dependsOn: 'decumulative' },
  closeGateThreeAdditionalPNo: { type: 'outputPorts', dependsOn: 'decumulative' },
  linkedPNos: {
    label: 'Linked PNos',
    type: 'array',
    col: 12,
    defaultValue: FEATURE_LINKED_PNO ? {
      sourceField: '',
      targetPNo: 0,
    } : [],
    fieldOptions: {
      sourceField: [
        'openGateOnePNo',
        'closeGateOnePNo',
      ],
    },
    hidden: !FEATURE_LINKED_PNO,
  },
};

export const Screw = {
  type: { type: 'screwMode', label: 'Mode' },
  switchOnePNo: { type: 'outputPorts', label: 'Switch On', required: true },
  // switchTwoPNo: 'outputPorts',
  switchOffPNo: { type: 'outputPorts', label: 'Switch Off' },
  onDelay: { type: 'number', unit: 'sec', min: 0, max: 25, defaultValue: 0 },
  offDelay: { type: 'number', unit: 'sec', min: 0, max: 1200, defaultValue: 0 },
  emergencyExemption: { type: 'boolean', defaultValue: false },
  icon: { type: 'iconList', defaultValue: IconList.DEFAULT },
};

export const Switch = {
  onPNo: { type: 'outputPorts' },
  offPNo: 'outputPorts',
  statusPNo: 'inputPorts',
  onLabel: { type: 'text', defaultValue: 'ON' },
  offLabel: { type: 'text', defaultValue: 'OFF' },
  onColor: { type: 'color', defaultValue: '#00ff00' },
  offColor: { type: 'color', defaultValue: '#ff0000' },
  offTriggerPNo: 'inputPorts',
  onTriggerPNo: 'inputPorts',
  onDelay: { type: 'number', unit: 'sec', min: 0, max: 25, defaultValue: 0 },
  offDelay: { type: 'number', unit: 'sec', min: 0, max: 1200, defaultValue: 0 },
  mode: { type: 'switchMode', required: true, defaultValue: SwitchMode.Maintain },
  emergencyExemption: { type: 'boolean', defaultValue: false },
};

export const Tank = {
  gatePNo: { type: 'outputPorts', required: true },
  closeStatusPNo: { type: 'inputPorts', required: true },
  emptyStatusPNo: 'inputPorts',
  emptyClearTime: { type: 'number', unit: 'sec', min: 0, max: 100 },
  emergencyExemption: { type: 'boolean', defaultValue: false },
  linkedPNos: {
    label: 'Linked PNos',
    type: 'array',
    col: 12,
    defaultValue: FEATURE_LINKED_PNO ? {
      sourceField: '',
      targetPNo: 0,
    } : [],
    fieldOptions: {
      sourceField: ['gatePNo'],
    },
    hidden: !FEATURE_LINKED_PNO,
  },
};

export const TruckDevice = {
  direction: { type: 'iconDirection', defaultValue: IconDirection.Left, required: true },
  logo: { type: 'boolean', defaultValue: false },
};

export const Vibrator = {
  switchOnePNo: { type: 'outputPorts', required: true },
  switchTwoPNo: 'outputPorts',
  mode: { type: 'vibratorMode', required: true, defaultValue: VibratorMode.SetPoint }, // 2 - Vib on fast flow
  emergencyExemption: { type: 'boolean', defaultValue: false },
  icon: { type: 'iconList', defaultValue: IconList.DEFAULT },
  linkedPNos: {
    label: 'Linked PNos',
    type: 'array',
    col: 12,
    defaultValue: FEATURE_LINKED_PNO ? {
      sourceField: '',
      targetPNo: 0,
    } : [],
    fieldOptions: {
      sourceField: ['switchOnePNo'],
    },
    hidden: !FEATURE_LINKED_PNO,
  },
};

export const Scoreboard = {
  portCOM: { type: 'number', defaultValue: 0, step: 1 },
  baudRate: { type: 'baudRate', defaultValue: BaudRate['1200'] },
  dataBits: { type: 'number', defaultValue: 8, step: 1 },
  parity: { type: 'parity', defaultValue: Parity.None },
  numberOfDigits: { type: 'number', defaultValue: 4, min: 2, max: 10, step: 1 },
  numberOfRows: { type: 'number', defaultValue: 3, min: 1, max: 6, step: 1 },
};

export const DeviceModels = {
  Aerator,
  AnalogInput,
  AnalogSwitch,
  Belt,
  Bin,
  Boot,
  Diverter,
  DustCollector,
  Hoist,
  Horn,
  ManualPanel,
  Mixer,
  Scoreboard,
  SlumpMeter,
  Pump,
  RemoteDisplay,
  Scale,
  Screw,
  Switch,
  Tank,
  TruckDevice,
  Vibrator,
};
